export interface UnitCost {
  compute?: number
  data?: number
  energy?: number
}

export interface UnitType {
  id: string
  name: string
  description: string
  hp: number
  attack: number
  attackSpeed: number  // attacks per second
  speed: number        // tiles per second
  range: number        // in tiles
  sight: number        // vision radius in tiles
  cost: UnitCost
  buildTime: number    // seconds
  canBuild: boolean
  canGather: boolean
  abilities?: string[]
  aoeRadius?: number
}

export const UNIT_TYPES: Record<string, UnitType> = {
  WORKER: {
    id: 'WORKER',
    name: 'Worker Instance',
    description: 'Basic worker unit. Can gather resources and construct buildings.',
    hp: 50,
    attack: 5,
    attackSpeed: 0.8,
    speed: 2,
    range: 1,
    sight: 5,
    cost: { compute: 50 },
    buildTime: 15,
    canBuild: true,
    canGather: true
  },
  SCOUT: {
    id: 'SCOUT',
    name: 'Scout Instance',
    description: 'Fast reconnaissance unit with extended sight range.',
    hp: 30,
    attack: 3,
    attackSpeed: 1.2,
    speed: 4,
    range: 1,
    sight: 10,
    cost: { compute: 40, data: 20 },
    buildTime: 12,
    canBuild: false,
    canGather: false
  },
  WARRIOR: {
    id: 'WARRIOR',
    name: 'Safety Enforcer',
    description: 'Heavy melee unit with high HP and damage.',
    hp: 120,
    attack: 18,
    attackSpeed: 0.7,
    speed: 1.5,
    range: 1,
    sight: 5,
    cost: { compute: 80, energy: 30 },
    buildTime: 20,
    canBuild: false,
    canGather: false,
    abilities: ['shield']
  },
  ARCHER: {
    id: 'ARCHER',
    name: 'Context Sniper',
    description: 'Ranged unit with long attack range but low HP.',
    hp: 45,
    attack: 14,
    attackSpeed: 0.6,
    speed: 1.8,
    range: 6,
    sight: 8,
    cost: { compute: 60, data: 40 },
    buildTime: 18,
    canBuild: false,
    canGather: false
  },
  REASONER: {
    id: 'REASONER',
    name: 'Extended Thinker',
    description: 'Powerful area-of-effect unit. Deals splash damage.',
    hp: 90,
    attack: 30,
    attackSpeed: 0.4,
    speed: 1,
    range: 4,
    sight: 6,
    cost: { compute: 150, data: 100, energy: 50 },
    buildTime: 35,
    canBuild: false,
    canGather: false,
    abilities: ['aoe'],
    aoeRadius: 2
  },
  HERO_OPUS: {
    id: 'HERO_OPUS',
    name: 'Opus Prime',
    description: 'Legendary hero unit for Opus faction. Extreme power with long thinking delays.',
    hp: 300,
    attack: 60,
    attackSpeed: 0.25,
    speed: 0.8,
    range: 3,
    sight: 8,
    cost: { compute: 400, data: 300, energy: 200 },
    buildTime: 60,
    canBuild: false,
    canGather: false,
    abilities: ['aoe', 'extendedThinking']
  },
  HERO_SONNET: {
    id: 'HERO_SONNET',
    name: 'Sonnet Paragon',
    description: 'Legendary hero unit for Sonnet faction. Gains strength from nearby allies.',
    hp: 200,
    attack: 35,
    attackSpeed: 0.8,
    speed: 2,
    range: 2,
    sight: 10,
    cost: { compute: 350, data: 250, energy: 150 },
    buildTime: 50,
    canBuild: false,
    canGather: false,
    abilities: ['contextAura']
  },
  HERO_HAIKU: {
    id: 'HERO_HAIKU',
    name: 'Haiku Swarmkeeper',
    description: 'Legendary hero unit for Haiku faction. Spawns swarmlings on attack.',
    hp: 150,
    attack: 20,
    attackSpeed: 1.5,
    speed: 2.5,
    range: 1,
    sight: 7,
    cost: { compute: 300, data: 200, energy: 100 },
    buildTime: 40,
    canBuild: false,
    canGather: false,
    abilities: ['spawnSwarm']
  }
}

export function getUnitType(id: string): UnitType {
  return UNIT_TYPES[id]
}

export function getUnitCost(id: string, factionCostMod: number = 1): UnitCost {
  const unit = UNIT_TYPES[id]
  if (!unit) return {}

  const cost: UnitCost = {}
  if (unit.cost.compute) cost.compute = Math.floor(unit.cost.compute * factionCostMod)
  if (unit.cost.data) cost.data = Math.floor(unit.cost.data * factionCostMod)
  if (unit.cost.energy) cost.energy = Math.floor(unit.cost.energy * factionCostMod)
  return cost
}
